import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, takeUntil } from 'rxjs';
import {
  Sale,
  SaleSubmissions,
  SaleSubmissionsStatusIds,
  SaleSubmissionsTableFields,
  SaleTabs,
} from 'src/app/models/sales/sales.model';
import { SalesService } from 'src/app/services/sales.service';
import { ToastService } from 'src/app/shared-new/services/toast.service';
import { SaleHorseSubmissionsStatusIds } from '../../models/sales/sale-horse.model';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { EventsEmitter } from '../../shared/event-emitter/EventEmitter';
import { SaleSubmissionsPopupComponent } from '../sale-submissions-popup/sale-submissions-popup.component';
import { SaleSubmissionsDataSource } from './sale-submissions-datasource';

@Component({
  selector: 'app-sale-submissions',
  templateUrl: './sale-submissions.component.html',
  styleUrls: ['./sale-submissions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaleSubmissionsComponent
  extends BaseUnsubscribeComponent
  implements OnInit {
  @Input() public saleId: number;
  public _sale: Sale;
  public lotOrHipText: string;
  private lotOrHipPascalCase: string;
  public tooltips = {
    all: '',
    approved: '',
    denied: '',
    inReview: ''
  };
  @Input() set sale(value: Sale) {
    this._sale = value;
    this.lotOrHipText = value?.lotOrHipText;
    this.lotOrHipPascalCase = this.toPascalCase(value?.lotOrHipText);
  }

  @Output() public readonly approveDeniedSubmissionsEvent = new EventEmitter();
  @Output() public readonly openStudyFilePopupByHorseId = new EventEmitter();

  public saleTabs = SaleTabs;
  public saleSubmissionsTableFields = SaleSubmissionsTableFields;
  public saleSubmissionsStatusIds = SaleSubmissionsStatusIds;

  public dataSource: SaleSubmissionsDataSource;
  @ViewChild(MatTable) table: MatTable<SaleSubmissions>;
  @ViewChild(MatSort) subsort: MatSort;
  public displayedColumns$ = new BehaviorSubject([
    'lotNumber',
    'vetName',
    'data',
    'dateTime',
    'status',
    'comment',
    'action',
  ]);

  public submissionsTab = this.saleSubmissionsStatusIds.IN_REVIEW;
  isSubmitLoader = false;
  public closeResult = '';

  constructor(
    private readonly saleService: SalesService,
    private readonly modalService: NgbModal,
    private eventEmitter: EventsEmitter,
    private changeDetectorRefs: ChangeDetectorRef,
    private toastService: ToastService,
  ) {
    super();
  }

  private toPascalCase(input: string): string {
    if (!input) {
      return '';
    }
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase()
  }

  public getApprovedCount(): string {
    const approved = this._saleSubmissions?.filter(
      (x) => x.statusId == this.saleSubmissionsStatusIds.APPROVED
    );
    const horse = approved?.length ? new Set(approved.map(h => h.lot)).size : 0;
    return `${approved?.length}/${horse}`;
  }

  public getDeniedCount(): string {
    const denied = this._saleSubmissions?.filter(
      (x) => x.statusId == this.saleSubmissionsStatusIds.DENIED
    );
    const horse = denied?.length ? new Set(denied.map(h => h.lot)).size : 0;
    return `${denied?.length}/${horse}`;
  }

  public getAllCount(): string {
    const horse = this._saleSubmissions?.length ? new Set(this._saleSubmissions.map(h => h.lot)).size : 0;
    return `${this._saleSubmissions?.length}/${horse}`;
  }

  public getInReviewCount(): string {
    const invited = this._saleSubmissions?.filter(
      (x) => x.statusId == this.saleSubmissionsStatusIds.IN_REVIEW
    );
    const horse = invited?.length ? new Set(invited.map(h => h.lot)).size : 0;
    return `${invited?.length}/${horse}`;
  }

  private generateTooltip(submissions: any[], label: string): string {
    const horseCount = submissions?.length
      ? new Set(submissions.map(h => h.lot)).size
      : 0;

    const labelPascal = this.toPascalCase(label);
    const plural = horseCount === 1 ? labelPascal : labelPascal + 's';

    return `${horseCount} ${plural}, ${submissions?.length ?? 0} total submissions`;
  }

  private updateTooltips(): void {
    const all = this._saleSubmissions ?? [];
    const approved = all.filter(x => x.statusId === this.saleSubmissionsStatusIds.APPROVED);
    const denied = all.filter(x => x.statusId === this.saleSubmissionsStatusIds.DENIED);
    const inReview = all.filter(x => x.statusId === this.saleSubmissionsStatusIds.IN_REVIEW);

    const label = this.lotOrHipPascalCase || 'Hip'; // fallback if undefined

    this.tooltips.all = this.generateTooltip(all, label);
    this.tooltips.approved = this.generateTooltip(approved, label);
    this.tooltips.denied = this.generateTooltip(denied, label);
    this.tooltips.inReview = this.generateTooltip(inReview, label);
  }

  public onTableScroll(): void {
    this.dataSource.onScroll();
  }

  public load(tab: SaleSubmissionsStatusIds): void {

    const data =
      tab === this.saleSubmissionsStatusIds.ALL
        ? this._saleSubmissions
        : this._saleSubmissions?.filter((x) => x.statusId == this.submissionsTab);

    if (data) {
      setTimeout(() => {
        if (this.subsort) {
          this.subsort.active = 'dateTime';
          this.subsort.direction = 'desc';
          this.dataSource = new SaleSubmissionsDataSource(data, this.subsort);
          this.dataSource.searchTable(this._searchTerm);
          this.subsort.sortChange.emit(this.subsort);
        }
      });
    }

  }

  public onClickTab(tab: SaleSubmissionsStatusIds) {
    this.submissionsTab = tab;

    if (this.saleSubmissionsStatusIds.ALL === tab) {
      this.load(this.saleSubmissionsStatusIds.ALL);
    }

    if (this.saleSubmissionsStatusIds.DENIED === tab) {
      this.load(this.saleSubmissionsStatusIds.DENIED);
    }

    if (this.saleSubmissionsStatusIds.APPROVED === tab) {
      this.load(this.saleSubmissionsStatusIds.APPROVED);
    }

    if (this.saleSubmissionsStatusIds.IN_REVIEW === tab) {
      this.load(this.saleSubmissionsStatusIds.IN_REVIEW);
    }
  }

  public ngOnInit(): void {
    this.load(this.saleSubmissionsStatusIds.IN_REVIEW);
    this.updateTooltips();
  }
  public _tab: string;
  @Input() set tab(value: string) {
    this._tab = value;
    this.submissionsTab = this.saleSubmissionsStatusIds.IN_REVIEW;
    if (value === this.saleTabs.SUBMISSIONS) {
      this.load(this.saleSubmissionsStatusIds.IN_REVIEW);
    }
  }

  public _saleSubmissions: SaleSubmissions[];
  @Input() set saleSubmissions(value: SaleSubmissions[]) {
    this._saleSubmissions = value;
    if (typeof value !== 'undefined') {
      this.load(this.submissionsTab);
    }
  }

  private _searchTerm: string;
  @Input() set searchTerm(value: string) {
    this._searchTerm = value;
    if (!this.dataSource) {
      this.load(this.submissionsTab);
    }
    this.dataSource?.searchTable(value);
  }




  public approve(saleSubmissions: SaleSubmissions) {
    this.isSubmitLoader = true;

    const acceptRequestData = {
      id: saleSubmissions.id,
      statusId: this.saleSubmissionsStatusIds.APPROVED,
      comments: '',
    };

    this.saleService
      .postSaleSubmissions(acceptRequestData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        this.isSubmitLoader = false;
        this.toastService.success('Submission request has been approved');
        this.onClickTab(this.submissionsTab);
        this.approveDeniedSubmissionsEvent.emit({
          response: response,
          type: 'approved',
        });
      });
  }

  public denied(saleSubmissions: SaleSubmissions) {
    const modalRef = this.modalService.open(SaleSubmissionsPopupComponent, {
      ariaLabelledBy: this.saleId + 'Submissions-Denied-Id',
      centered: true,
      size: 'md',
      keyboard: false,
      backdrop: 'static',
    });

    modalRef.componentInstance.data = {
      saleId: this.saleId,
      saleSubmissions: saleSubmissions,
    };

    modalRef.result.then(
      (result) => {
        const data = JSON.parse(result);

        this.isSubmitLoader = true;

        const deniedRequestData = {
          id: data.id,
          statusId: this.saleSubmissionsStatusIds.DENIED,
          comments: data.comments,
        };

        this.saleService
          .postSaleSubmissions(deniedRequestData)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((response) => {
            this.isSubmitLoader = false;
            this.toastService.success('Submission request has been denied');
            this.onClickTab(this.submissionsTab);
            this.approveDeniedSubmissionsEvent.emit({
              response: response,
              type: 'denied',
            });
          });
      },
      (reason) => {
        this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      }
    );
  }

  openStudyFilePopup(horseId: number, submissionId: number): void {
    this.openStudyFilePopupByHorseId.emit({ horseId, submissionId });
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }
}
